// Impersonation Service for Tenant Admin
// Handles switching between admin and user contexts

import axiosInstance from '../config/axios.config';

class ImpersonationService {
    constructor() {
        this.listeners = [];
    }

    /**
     * Start impersonating a user
     */
    async startImpersonation(userId) {
        try {
            console.log('=== IMPERSONATION START ===');
            console.log('Target user ID:', userId);

            // Store current admin context before impersonation
            const currentAuth = {
                token: localStorage.getItem('authToken'),
                user: localStorage.getItem('user'),
                isAuthenticated: localStorage.getItem('isAuthenticated'),
                userRoles: localStorage.getItem('userRoles'),
            };

            console.log('Current admin context stored');

            // Request impersonation from backend
            const response = await axiosInstance.post(`/admin/impersonate/${userId}`);

            if (response.data.success) {
                // Store original admin data for restoration
                localStorage.setItem('originalAdminToken', currentAuth.token);
                localStorage.setItem('originalAdminUser', currentAuth.user);
                localStorage.setItem('originalAdminAuth', currentAuth.isAuthenticated);
                localStorage.setItem('originalAdminRoles', currentAuth.userRoles || '[]');

                // Switch to impersonated user context
                localStorage.setItem('authToken', response.data.token);
                localStorage.setItem('user', JSON.stringify(response.data.user));
                localStorage.setItem('isAuthenticated', 'true');
                localStorage.setItem('isImpersonating', 'true');
                localStorage.setItem('impersonatedUserId', response.data.user.id);
                localStorage.setItem('impersonatedUserName', response.data.user.name);

                // Update userRoles so the menu reflects the impersonated user's role
                if (response.data.roles) {
                    localStorage.setItem('userRoles', JSON.stringify(response.data.roles));
                }

                // Store original admin info for display
                if (response.data.original_admin) {
                    localStorage.setItem('originalAdminInfo', JSON.stringify(response.data.original_admin));
                }

                console.log('Switched to impersonated user:', response.data.user.name);
                console.log('==============================');

                // Notify all listeners about auth state change
                this.notifyAuthStateChange();

                return {
                    success: true,
                    user: response.data.user,
                    message: response.data.message
                };
            } else {
                throw new Error(response.data.message || 'Failed to start impersonation');
            }

        } catch (error) {
            console.error('Impersonation failed:', error);
            
            const errorMessage = error.response?.data?.message || 
                               error.message || 
                               'Failed to start impersonation';
            
            throw new Error(errorMessage);
        }
    }

    /**
     * Stop impersonating and return to admin context
     */
    async stopImpersonation() {
        try {
            console.log('=== IMPERSONATION STOP ===');

            // Call backend to log impersonation end (optional)
            try {
                await axiosInstance.post('/admin/stop-impersonation');
            } catch (error) {
                console.warn('Failed to notify backend about impersonation stop:', error);
                // Continue with local cleanup even if backend call fails
            }

            // Restore original admin context
            const originalToken = localStorage.getItem('originalAdminToken');
            const originalUser = localStorage.getItem('originalAdminUser');
            const originalAuth = localStorage.getItem('originalAdminAuth');
            const originalRoles = localStorage.getItem('originalAdminRoles');

            if (originalToken && originalUser) {
                localStorage.setItem('authToken', originalToken);
                localStorage.setItem('user', originalUser);
                localStorage.setItem('isAuthenticated', originalAuth || 'true');
                if (originalRoles) {
                    localStorage.setItem('userRoles', originalRoles);
                }

                // Clean up impersonation data
                this.clearImpersonationData();

                console.log('Restored to admin context');
                console.log('==========================');

                // Notify all listeners about auth state change
                this.notifyAuthStateChange();

                return { success: true, message: 'Returned to admin view' };
            } else {
                throw new Error('Could not find original admin context');
            }

        } catch (error) {
            console.error('Stop impersonation failed:', error);
            
            // Fallback: try to clear impersonation data anyway
            this.clearImpersonationData();
            
            throw new Error(error.message || 'Failed to stop impersonation');
        }
    }

    /**
     * Check if currently impersonating
     */
    isImpersonating() {
        return localStorage.getItem('isImpersonating') === 'true';
    }

    /**
     * Get impersonated user info
     */
    getImpersonatedUser() {
        if (!this.isImpersonating()) {
            return null;
        }

        return {
            id: localStorage.getItem('impersonatedUserId'),
            name: localStorage.getItem('impersonatedUserName'),
            user: JSON.parse(localStorage.getItem('user') || '{}')
        };
    }

    /**
     * Get original admin user info
     */
    getOriginalAdmin() {
        const adminInfo = localStorage.getItem('originalAdminInfo');
        return adminInfo ? JSON.parse(adminInfo) : null;
    }

    /**
     * Get list of users that can be impersonated
     * Supports pagination and search
     *
     * @param {Object} params - Query parameters
     * @param {number} params.page - Current page (1-based)
     * @param {number} params.per_page - Items per page
     * @param {string} params.search - Search query
     * @param {string} params.sort_field - Field to sort by
     * @param {string} params.sort_order - Sort order (asc/desc)
     */
    async getImpersonatableUsers(params = {}) {
        try {
            const queryParams = new URLSearchParams();

            if (params.page) queryParams.append('page', params.page);
            if (params.per_page) queryParams.append('per_page', params.per_page);
            if (params.search) queryParams.append('search', params.search);
            if (params.sort_field) queryParams.append('sort_field', params.sort_field);
            if (params.sort_order) queryParams.append('sort_order', params.sort_order);

            const queryString = queryParams.toString();
            const url = `/admin/impersonatable-users${queryString ? `?${queryString}` : ''}`;

            const response = await axiosInstance.get(url);

            if (response.data.success) {
                return {
                    success: true,
                    users: response.data.users,
                    pagination: response.data.pagination
                };
            } else {
                throw new Error(response.data.message || 'Failed to fetch users');
            }

        } catch (error) {
            console.error('Failed to get impersonatable users:', error);

            const errorMessage = error.response?.data?.message ||
                               error.message ||
                               'Failed to fetch users';

            throw new Error(errorMessage);
        }
    }

    /**
     * Clear all impersonation related data
     */
    clearImpersonationData() {
        localStorage.removeItem('isImpersonating');
        localStorage.removeItem('impersonatedUserId');
        localStorage.removeItem('impersonatedUserName');
        localStorage.removeItem('originalAdminToken');
        localStorage.removeItem('originalAdminUser');
        localStorage.removeItem('originalAdminAuth');
        localStorage.removeItem('originalAdminRoles');
        localStorage.removeItem('originalAdminInfo');
    }

    /**
     * Subscribe to auth state changes
     */
    onAuthStateChange(callback) {
        this.listeners.push(callback);
        
        // Return unsubscribe function
        return () => {
            this.listeners = this.listeners.filter(listener => listener !== callback);
        };
    }

    /**
     * Notify all listeners about auth state change
     */
    notifyAuthStateChange() {
        this.listeners.forEach(callback => {
            try {
                callback({
                    isImpersonating: this.isImpersonating(),
                    impersonatedUser: this.getImpersonatedUser(),
                    originalAdmin: this.getOriginalAdmin()
                });
            } catch (error) {
                console.error('Error in auth state change listener:', error);
            }
        });
    }

    /**
     * Emergency cleanup - use if impersonation gets stuck
     */
    emergencyStopImpersonation() {
        console.warn('Emergency impersonation stop initiated');
        
        try {
            // Restore original admin context if available
            const originalToken = localStorage.getItem('originalAdminToken');
            const originalUser = localStorage.getItem('originalAdminUser');
            const originalRoles = localStorage.getItem('originalAdminRoles');

            if (originalToken && originalUser) {
                localStorage.setItem('authToken', originalToken);
                localStorage.setItem('user', originalUser);
                localStorage.setItem('isAuthenticated', 'true');
                if (originalRoles) {
                    localStorage.setItem('userRoles', originalRoles);
                }
            }
            
            // Clear all impersonation data
            this.clearImpersonationData();
            
            // Notify listeners
            this.notifyAuthStateChange();
            
            console.log('Emergency cleanup completed');
            return { success: true, message: 'Emergency cleanup completed' };
            
        } catch (error) {
            console.error('Emergency cleanup failed:', error);
            return { success: false, message: 'Emergency cleanup failed' };
        }
    }
}

export default new ImpersonationService();